import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearLegacyIubendaCookies, loadConsent, retryPendingConsentLog, saveConsent, sendConsentLog } from '../legal/cookieConsent';

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => loadConsent());
  const [bannerOpen, setBannerOpen] = useState(() => loadConsent() == null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    clearLegacyIubendaCookies();
    retryPendingConsentLog();
  }, []);

  const persist = useCallback((partial) => {
    const saved = saveConsent(partial);
    setConsent(saved);
    setBannerOpen(false);
    setPreferencesOpen(false);
    sendConsentLog(saved);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ choice: 'all' });
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ choice: 'rejected' });
  }, [persist]);

  const savePreferences = useCallback(() => {
    persist({ choice: 'custom' });
  }, [persist]);

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
    setBannerOpen(false);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
    if (!loadConsent()) setBannerOpen(true);
  }, []);

  const value = useMemo(() => ({
    consent,
    bannerOpen,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  }), [
    consent,
    bannerOpen,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  ]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent deve essere usato dentro CookieConsentProvider');
  }
  return ctx;
}
